import { useMemo } from 'react'

import { Link, T } from 'govwell-ui'
import { EntityLayerType } from 'types/graphql'

import { routes } from '@redwoodjs/router'

import { EntityLayerTypes } from 'src/utils/entityDisplayHelpers'

/** Minimal entity shape for staff deep links from tables and universal search. */
export type EntityUrlFragment = {
  name?: string | null
  uuid: string
  layer?: {
    type?: EntityLayerType | null
    uuid?: string | null
    name?: string | null
  } | null
}

type Props = {
  entity?: EntityUrlFragment | null
  displayName?: string | null
  /** Tailwind `max-w-[…]` class forwarded from `TableViewCell`. */
  maxWidthClassName?: string
}

export const EntityDisplay = ({ entity, displayName, maxWidthClassName }: Props) => {
  const content = useMemo(() => {
    if (entity) {
      const url = entityUrl(entity)
      return url ? (
        <Link internalRoute={url}>{displayName ?? entity.name}</Link>
      ) : (
        (displayName ?? entity.name)
      )
    } else if (displayName) {
      return displayName
    }
    return '-'
  }, [entity, displayName])

  return <T.Body className={maxWidthClassName}>{content}</T.Body>
}

export const entityUrl = (entity: EntityUrlFragment): string | null => {
  if (entity.layer?.type === EntityLayerTypes.Address) {
    return routes.entityAddress({ addressUuid: entity.uuid })
  }
  if (entity.layer?.type === EntityLayerTypes.Parcel) {
    return routes.entityParcel({ parcelUuid: entity.uuid })
  }
  if (entity.layer?.type === EntityLayerTypes.Other && entity.layer.uuid) {
    return routes.otherLayerEntityDetail({
      entityLayerUuid: entity.layer.uuid,
      entityUuid: entity.uuid,
    })
  }
  return null
}

/** Deep-link to the entity page with the Conditions tab selected (see EntityAddressPage / EntityParcelPage). */
export const entityUrlWithConditionsTab = (entity: EntityUrlFragment): string | null => {
  const base = entityUrl(entity)
  if (!base) {
    return null
  }
  return `${base}?tabKey=conditions`
}
